import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { DesktopLayoutSizes } from 'tripPlanner/components/ItemCard/Common/utils'
import useClickOutsideBox from 'tripPlanner/components/TripMaps/hooks/useClickOutsideBox'
import TripItemSummaryCard from 'tripPlanner/components/TripMaps/SummaryCards/TripItem/TripItemSummaryCard'
import TripContext, { TripContextValue } from 'tripPlanner/contexts/TripContext'
import { useTrips } from 'tripPlanner/hooks/api/trip'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { TripItem } from 'tripPlanner/types/tripItem'

const TripItemCardContainer = styled.div`
  position: absolute;
  z-index: 5;
  padding: ${rem(16)};
  bottom: 0;
  width: 100%;

  ${DesktopLayoutSizes} {
    padding: ${rem(24)};
  }
`

interface Props {
  selectedItem: TripItem
  onDeselect: () => void
  containerRef?: React.RefObject<HTMLDivElement>
  cardRef: React.MutableRefObject<HTMLDivElement | null>
}

function TripItemCardSingle({
  selectedItem,
  onDeselect,
  containerRef,
  cardRef,
}: Props) {
  // Pass this ref into the TripItemSummaryCard card so that so the deselecting logic only occurs
  // when it's from the inner container (non-padding area of the card)

  useClickOutsideBox(cardRef, onDeselect, { excludeModalClicks: true, excludePortaledDropdownClicks: true })

  const permissions = useTripPermissions(selectedItem.tripId)
  const { data: trips } = useTrips()

  const tripContextValue = useMemo<TripContextValue>(
    () => ({
      tripId: selectedItem.tripId,
      permissions,
      isPublicTrip: false,
      name: trips.find(trip => trip.id === selectedItem.tripId)?.name ?? '', // the name of the trip doesn't really matter here.
      displayContext: 'search',
    }),
    [permissions, selectedItem.tripId, trips],
  )

  return (
    <TripContext.Provider value={tripContextValue}>
      <TripItemCardContainer ref={containerRef}>
        <TripItemSummaryCard
          item={selectedItem}
          ref={cardRef}
          showCloseButton
          onClose={onDeselect}
        />
      </TripItemCardContainer>
    </TripContext.Provider>
  )
}

export default TripItemCardSingle
